import { useCallback, useEffect, useState } from 'react';
import { Boxes, ChevronDown, ChevronRight, Eye, EyeOff, Pencil, Ruler, Trash2 } from 'lucide-react';
import type { CategoriaVista, GrupoDeVentaVista } from '@shared/types';
import { formatPrecio } from '@renderer/utils/formatters';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { EditarGrupoModal } from './EditarGrupoModal';

/**
 * Sprint 84 — Lo que se vende, **como el negocio lo capturó**.
 *
 * Reporte del dueño, y es un rompimiento de nuestra propia lógica: dio de alta "Frijoles Charros ·
 * litro · 1 lt y 1/2 lt" como **una cosa**, y al volver a Productos se encontró dos renglones
 * sueltos que le preguntaban *"¿de qué ingrediente sale?"* y *"¿cuánto lleva cada venta?"*. Su
 * pregunta fue exacta: **"¿y ahora dónde quedó Frijoles Charros donde se veían agrupados?"**.
 *
 * Y el daño no era sólo estético: cuando se dio cuenta de que también vendía 3/4 y 1/4, **no tenía
 * dónde agregarlos**. El alta ya había pasado y la edición hablaba otro idioma.
 *
 * Aquí se ve lo mismo que capturó, se edita igual que lo capturó, y los tamaños se agregan donde
 * uno los busca: dentro del producto.
 */
export function GruposDeVenta({
  categorias,
  onCambio,
  busquedaInicial = '',
}: {
  categorias: CategoriaVista[];
  onCambio: () => void;
  /** Sprint 110 — Con qué llega quien viene desde Existencias señalando algo concreto. */
  busquedaInicial?: string;
}) {
  const [grupos, setGrupos] = useState<GrupoDeVentaVista[]>([]);
  const [cargando, setCargando] = useState(true);
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const [editando, setEditando] = useState<GrupoDeVentaVista | null>(null);
  const [busqueda, setBusqueda] = useState(busquedaInicial);

  const cargar = useCallback(async () => {
    const res = await window.api.productos.grupos();
    if (res.ok) setGrupos(res.data);
    setCargando(false);
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const alternar = (clave: string) => {
    setAbiertos((prev) => {
      const s = new Set(prev);
      if (s.has(clave)) s.delete(clave);
      else s.add(clave);
      return s;
    });
  };

  const visibles = grupos.filter((g) =>
    busqueda.trim() ? g.nombre.toLowerCase().includes(busqueda.trim().toLowerCase()) : true,
  );

  /**
   * Sprint 109 — "Hoy no lo tengo", en un toque y desde donde se mira el menú.
   *
   * Petición del negocio: poder marcar algo como **no disponible** en vez de borrarlo —y que
   * desaparezca del enlace que le manda al cliente—. Lo que faltaba **no era el mecanismo**:
   * `habilitado` existe desde el primer sprint, lo respetan el punto de venta, el menú en línea,
   * los paquetes y el cobro. Lo que faltaba era el lugar y el nombre: sólo se alcanzaba desde un
   * checkbox llamado *"Se puede vender ahora"*, escondido al fondo de un formulario de edición.
   *
   * Se evaluó agregar un estado nuevo ("pausado", distinto de "retirado") y se descartó: el sistema
   * los trataría idéntico en las seis capas que ya miran `habilitado`, así que sería un estado más
   * que decidir en hora pico a cambio de nada. Y un estado que se comporta igual que otro es un
   * estado que alguien va a usar mal.
   *
   * Apagar un grupo apaga todos sus tamaños: *"no tengo pollo"* no admite que el medio pollo siga
   * a la venta.
   */
  const alternarDisponible = async (g: GrupoDeVentaVista, disponible: boolean) => {
    if (!disponible) {
      const ok = await window.confirmar({
        titulo: `¿Hoy no hay ${g.nombre}?`,
        mensaje:
          'Desaparece del punto de venta y del menú que le mandas a tus clientes. Lo vendido se ' +
          'conserva y lo vuelves a encender cuando quieras.',
        textoConfirmar: 'Sí, hoy no lo tengo',
        textoCancelar: 'Cancelar',
        variant: 'warn',
      });
      if (!ok) return;
    }
    /*
      Si alguno falla se dice, y se dice **cuál**. Un interruptor que se queda a medias sin avisar
      deja el medio pollo a la venta cuando no hay pollo, y quien lo apretó se va convencida de que
      lo apagó. El resto sí se aplica: apagar cuatro de cinco es mejor que ninguno, y el que quedó
      se ve en la lista al recargar.
    */
    const fallaron: string[] = [];
    for (const f of g.formatos) {
      const r = await window.api.productos.actualizar(f.productoId, { habilitado: disponible });
      if (!r.ok) fallaron.push(f.nombre);
    }
    await cargar();
    onCambio();

    if (fallaron.length > 0) {
      await window.alertar({
        titulo: 'Quedó a medias',
        mensaje: `No se pudo cambiar ${fallaron.join(', ')}. Revisa la lista e inténtalo otra vez.`,
        variant: 'warn',
      });
    }
  };

  /**
   * Sprint 110 — "Ya no lo vendo": la baja, donde está el alta.
   *
   * Antes esto se hacía desde el bote de basura de la pantalla de existencias, que borraba **el
   * ingrediente y dejaba los productos colgando**. Así quedaron 27 productos a la venta descontando
   * de un almacén que ya no existía y 41 ingredientes invisibles bloqueando altas por nombre.
   *
   * Aquí se retira **la cosa completa**: el renglón del menú y lo que hay de él en el almacén.
   */
  const retirar = async (g: GrupoDeVentaVista) => {
    const ok = await window.confirmar({
      titulo: `¿Ya no vendes ${g.nombre}?`,
      mensaje:
        `Se quita de tu menú${g.formatos.length > 1 ? ` con sus ${g.formatos.length} tamaños` : ''} ` +
        'y también de tus existencias. Si ya lo habías vendido alguna vez, esas ventas se conservan.\n\n' +
        'Si sólo se te acabó hoy, usa "Hoy no lo tengo" en lugar de esto.',
      textoConfirmar: 'Sí, quitarlo del menú',
      textoCancelar: 'Cancelar',
      variant: 'destructive',
    });
    if (!ok) return;

    const r = await window.api.productos.retirarGrupo(
      g.insumoId !== null ? { insumoId: g.insumoId } : { productoId: g.formatos[0].productoId },
    );
    if (!r.ok) {
      await window.alertar({
        titulo: 'No se pudo quitar',
        mensaje: traducirErrorBackend(r.error, r.codigo),
        variant: 'warn',
      });
      return;
    }
    await cargar();
    onCambio();

    /*
      Se dice qué pasó de verdad, y no un "listo" a secas: si se ocultó en vez de borrarse, la
      persona tiene que saber por qué lo va a seguir viendo en sus reportes de meses pasados.
    */
    const { borrados, ocultados } = r.data;
    await window.alertar({
      titulo: `"${g.nombre}" ya no está en tu menú`,
      mensaje:
        ocultados > 0
          ? `Se ocultaron ${ocultados} porque ya se habían vendido: su historial se conserva.` +
            (borrados > 0 ? ` Los otros ${borrados} se borraron.` : '')
          : 'Se borró por completo, nunca se había vendido.',
      variant: 'success',
    });
  };

  if (cargando) return <p className="text-sm text-slate-500">Cargando…</p>;

  return (
    <div className="space-y-3">
      <input
        className="input max-w-sm"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar…"
      />

      {visibles.length === 0 ? (
        <p className="rounded-lg bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
          {grupos.length === 0
            ? 'Todavía no has dado de alta nada. Usa "Nuevo producto".'
            : 'Nada con ese nombre.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {visibles.map((g) => {
            const abierto = abiertos.has(g.clave);
            /**
             * Un solo tamaño que además es la unidad completa no necesita desplegarse: es una
             * lata, y desplegarla para ver "1 pieza" es ruido.
             */
            const simple = g.formatos.length === 1 && Math.abs(g.formatos[0].cantidad - 1) < 1e-6;
            /** Se vende hoy mientras quede al menos un tamaño encendido. */
            const seVende = g.formatos.some((f) => f.habilitado);
            return (
              <li
                key={g.clave}
                className={`rounded-xl border bg-white ${
                  seVende ? 'border-slate-200' : 'border-slate-300 bg-slate-50'
                }`}
              >
                <div className="flex flex-wrap items-center gap-3 p-3">
                  {!simple && (
                    <button
                      onClick={() => alternar(g.clave)}
                      className="btn-ghost p-1"
                      aria-label={abierto ? 'Ocultar tamaños' : 'Ver tamaños'}
                    >
                      {abierto ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className={`font-semibold ${seVende ? '' : 'text-slate-500'}`}>
                        {g.nombre}
                      </span>
                      <span className="text-xs uppercase text-slate-500">{g.unidad}</span>
                      {!seVende && (
                        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">
                          Hoy no lo tienes
                        </span>
                      )}
                      {!simple && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                          {g.formatos.length} tamaños
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      {g.llevaInventario ? (
                        <span className="flex items-center gap-1">
                          <Boxes className="h-3 w-3" />
                          Quedan {g.disponible} {g.unidad}
                          {g.mermaCoccionPct ? ` · merma ${g.mermaCoccionPct}%` : ''}
                        </span>
                      ) : (
                        <span>Sin control de existencias</span>
                      )}
                      {simple && <span>{formatPrecio(g.formatos[0].precio, '$')}</span>}
                    </div>
                  </div>

                  {/*
                    El botón vive en el grupo, no en cada tamaño: editar "Frijoles Charros" es una
                    sola acción, aunque por dentro toque dos productos.

                    Sprint 87 — Y sale **siempre**. Antes se escondía cuando el producto no llevaba
                    inventario, de modo que una salsa mal escrita no tenía dónde corregirse: no era
                    una decisión, era una consecuencia de cómo está partido el modelo por dentro.
                  */}
                  {/*
                    Sprint 109 — El interruptor de todos los días, al mismo tamaño que editar.

                    Marcar "hoy no lo tengo" pasa cuatro veces por semana; editar precios, cuatro
                    veces al año. Que el primero estuviera enterrado en el segundo es por qué el
                    negocio pidió "una forma de marcarlo no disponible" teniéndola desde siempre.
                  */}
                  <button
                    onClick={() => void alternarDisponible(g, !seVende)}
                    className={`btn-ghost min-h-[44px] border text-sm ${
                      seVende
                        ? 'border-slate-300'
                        : 'border-emerald-400 bg-emerald-50 font-semibold text-emerald-800'
                    }`}
                  >
                    {seVende ? (
                      <>
                        <EyeOff className="h-4 w-4" />
                        Hoy no lo tengo
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" />
                        Volver a venderlo
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setEditando(g)}
                    className="btn-ghost min-h-[44px] border border-slate-300 text-sm"
                  >
                    <Pencil className="h-4 w-4" />
                    {g.llevaInventario ? 'Editar tamaños y precios' : 'Editar'}
                  </button>

                  {/*
                    Sprint 110 — La baja definitiva, discreta y a la derecha del todo.

                    Se usa dos veces al año y borra cosas: no compite en tamaño con lo de todos los
                    días. Pero **existe aquí**, porque quitarla de Existencias sin ponerla en el
                    menú sería dejar al negocio sin poder limpiar su catálogo — el mismo error que
                    ya cometimos al revés.
                  */}
                  <button
                    onClick={() => void retirar(g)}
                    className="btn-ghost min-h-[44px] px-2 text-slate-400 hover:bg-rose-50 hover:text-rose-700"
                    title="Ya no lo vendo: quitarlo del menú"
                    aria-label={`Ya no vendo ${g.nombre}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {abierto && !simple && (
                  <ul className="divide-y divide-slate-100 border-t border-slate-100">
                    {g.formatos.map((f) => (
                      <li
                        key={f.productoId}
                        className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm"
                      >
                        <span className="flex items-center gap-2">
                          <Ruler className="h-3.5 w-3.5 text-slate-400" />
                          {f.nombre}
                          {!f.habilitado && (
                            <span className="rounded bg-slate-100 px-1.5 text-xs text-slate-500">
                              oculto
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-3 text-slate-600">
                          <span className="text-xs">
                            descuenta {f.cantidad} {g.unidad}
                          </span>
                          <span className="font-mono font-semibold">
                            {formatPrecio(f.precio, '$')}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <EditarGrupoModal
        grupo={editando}
        categorias={categorias}
        onCerrar={() => setEditando(null)}
        onGuardado={() => {
          setEditando(null);
          void cargar();
          onCambio();
        }}
      />
    </div>
  );
}
